import { useSession } from "@/store/session";
import type { AuthResponse } from "./types";

const BASE = import.meta.env.VITE_API_BASE_URL as string;

if (!BASE) {
  // Fail loudly at boot rather than making mysterious relative-URL requests.
  throw new Error("VITE_API_BASE_URL is not set. Check .env.development / .env.production.");
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type RequestOptions = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  auth?: boolean; // default true — send bearer token if present
  _retried?: boolean; // internal: prevents infinite refresh→retry loops
};

// Single-flight guard: concurrent 401s (or a boot-time refresh) share one
// in-flight refresh call instead of stampeding /v1/auth/refresh and rotating
// the refresh token multiple times.
let refreshPromise: Promise<boolean> | null = null;

// refreshAccessToken exchanges the stored refresh token for a fresh access +
// rotated refresh token. Returns true on success. On a definitive rejection the
// session is cleared. Safe to call concurrently: within a tab a single in-flight
// request is shared; across tabs a Web Lock serializes refreshes so concurrent
// page loads don't each rotate the token (and trip server reuse detection).
export function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = withRefreshLock(doRefresh).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// withRefreshLock serializes refreshes across tabs when the Web Locks API is
// available, falling back to a plain call where it isn't.
function withRefreshLock(fn: () => Promise<boolean>): Promise<boolean> {
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    // Narrow to just the call shape we use — the lib's LockGrantedCallback
    // typing varies across TS versions and rejects an async callback.
    const locks = (navigator as unknown as {
      locks: { request: (name: string, cb: () => Promise<boolean>) => Promise<boolean> };
    }).locks;
    return locks.request("submitterai.token-refresh", fn);
  }
  return fn();
}

async function doRefresh(): Promise<boolean> {
  try {
    // Pull the freshest persisted session first — another tab may have rotated
    // the token while we waited for the lock, in which case we use the new one.
    await useSession.persist.rehydrate();
    const session = useSession.getState();
    if (!session.hasValidRefresh()) return false;

    const res = await fetch(`${BASE}/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    });
    if (!res.ok) {
      // Refresh token is invalid/expired/revoked → force re-login.
      useSession.getState().clear();
      return false;
    }
    const payload = (await res.json()) as AuthResponse;
    useSession.getState().setSession(payload);
    return true;
  } catch {
    // Network error — keep the session so a later retry can recover.
    return false;
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, auth = true, _retried = false, ...rest } = opts;
  const headers = new Headers({ "Content-Type": "application/json" });
  const token = useSession.getState().token;
  if (auth && token) headers.set("Authorization", `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...rest,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError(
      e instanceof Error ? e.message : "network error",
      0,
      "network_error",
    );
  }

  // Access token expired mid-session: transparently refresh once and retry.
  if (
    res.status === 401 &&
    auth &&
    !_retried &&
    useSession.getState().hasValidRefresh()
  ) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...opts, _retried: true });
    }
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const msg = extractErrorMessage(payload) ?? res.statusText ?? "request failed";
    const code = extractErrorCode(payload);

    // 401 → nuke session and let the router send them to /login
    if (res.status === 401) {
      useSession.getState().clear();
    }
    throw new ApiError(msg, res.status, code);
  }

  return payload as T;
}

function extractErrorMessage(p: unknown): string | null {
  if (!p || typeof p !== "object") return typeof p === "string" ? p : null;
  const obj = p as Record<string, unknown>;
  if (typeof obj.message === "string") return obj.message;
  if (typeof obj.error === "string") return obj.error;
  return null;
}

function extractErrorCode(p: unknown): string | undefined {
  if (!p || typeof p !== "object") return undefined;
  const obj = p as Record<string, unknown>;
  if (typeof obj.code === "string") return obj.code;
  return undefined;
}
